const GOOGLE_ADS_ATTRIBUTION_PATH_PATTERN =
  /\/a\/(?!clear(?=$|[/?#\s"'`]))[^/?#\s"'`]+(?=$|[/?#\s"'`])/g;

export const REDACTED_GOOGLE_ADS_ATTRIBUTION_PATH = "/a/[redacted]";
export const REDACTED_GOOGLE_ADS_ATTRIBUTION_VALUE = "[redacted]";

const SENSITIVE_ATTRIBUTION_KEYS = new Set([
  "attributionsession",
  "googleadsautopilotattributionsession",
  "targetref",
]);

function isSensitiveAttributionKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return (
    SENSITIVE_ATTRIBUTION_KEYS.has(normalized) ||
    normalized === "cookie" ||
    normalized === "set-cookie" ||
    normalized.includes("google_ads_attribution")
  );
}

export function redactGoogleAdsAttributionPath(value: string): string {
  return value.replace(
    GOOGLE_ADS_ATTRIBUTION_PATH_PATTERN,
    REDACTED_GOOGLE_ADS_ATTRIBUTION_PATH,
  );
}

/** Scrub paths from every serializable Sentry field, including breadcrumbs. */
export function scrubGoogleAdsAttributionFromSentry<T>(payload: T): T {
  const clones = new WeakMap<object, object>();

  const scrub = (value: unknown, key = ""): unknown => {
    if (isSensitiveAttributionKey(key)) {
      return REDACTED_GOOGLE_ADS_ATTRIBUTION_VALUE;
    }
    if (typeof value === "string") {
      return redactGoogleAdsAttributionPath(value);
    }
    if (typeof value !== "object" || value === null) {
      return value;
    }

    const existingClone = clones.get(value);
    if (existingClone) return existingClone;

    if (Array.isArray(value)) {
      const clone: unknown[] = new Array(value.length);
      clones.set(value, clone);
      for (let index = 0; index < value.length; index += 1) {
        if (Object.prototype.hasOwnProperty.call(value, index)) {
          clone[index] = scrub(value[index]);
        }
      }
      return clone;
    }

    const clone: Record<string, unknown> =
      Object.getPrototypeOf(value) === null ? Object.create(null) : {};
    clones.set(value, clone);
    for (const [key, nested] of Object.entries(value)) {
      // Define an own data property so even unusual keys such as `__proto__`
      // cannot mutate the clone's prototype while telemetry is sanitized.
      Object.defineProperty(clone, key, {
        configurable: true,
        enumerable: true,
        value: scrub(nested, key),
        writable: true,
      });
    }
    return clone;
  };

  return scrub(payload) as T;
}
